import useToast from "@lib/hooks/useToast";
import type { UseNavigateResult } from "@tanstack/react-router";
import { useCallback } from "react";
import { ERROR_MESSAGES, TABLE_CONFIG } from "./constants";
import type { ErrorHandler, Logger } from "./types";

interface LocationState {
  pathname: string;
}

/** Error shape that includes an optional Axios-like response property */
interface ErrorWithResponse {
  name?: string;
  response?: {
    status: number;
  };
}

interface UseTableErrorHandlingOptions {
  /** Error handling mode: "toast", "silent", or a custom function */
  onError?: ErrorHandler;
  /** Suppress all toasts — log only */
  silent?: boolean;
  /** Navigation function for redirect on 403 */
  navigate?: UseNavigateResult<string>;
  /** Current location (provides pathname for redirect origin) */
  location?: LocationState;
  /** Custom logger; falls back to console.warn in silent mode */
  logger?: Logger | null;
}

/**
 * Hook for handling table errors with configurable modes.
 *
 * Modes:
 * - "toast" (default): shows a toast with a contextual message,
 *   redirects to /unauthorized on 403
 * - "silent": logs via the provided logger or console.warn, no UI feedback
 * - function: calls the custom handler directly
 */
export function useTableErrorHandling({
  onError = "toast",
  silent = false,
  navigate,
  location,
  logger,
}: UseTableErrorHandlingOptions) {
  const toast = useToast();

  const handleError = useCallback(
    (error: unknown) => {
      // Silent mode — log only, no UI feedback
      if (silent) {
        if (logger) {
          logger.error("Erreur Table (silencieuse):", error);
        } else {
          console.warn("Erreur Table (silencieuse):", error);
        }
        return;
      }

      // Custom handler provided by caller
      if (typeof onError === "function") {
        onError(error as Error);
        return;
      }

      // Default toast-based error handling
      if (onError === "toast") {
        let message: string = ERROR_MESSAGES.GENERIC_ERROR;
        let shouldRedirect = false;
        const err = error as ErrorWithResponse;

        if (err.response?.status === 403) {
          message = ERROR_MESSAGES.PERMISSION_DENIED;
          shouldRedirect = true;
        } else if (err.response?.status != null && err.response.status >= 500) {
          message = ERROR_MESSAGES.SERVER_ERROR;
        } else if (!err.response) {
          message = ERROR_MESSAGES.NETWORK_ERROR;
        }

        if (shouldRedirect && navigate && location) {
          navigate({ to: "/unauthorized", search: { from: location.pathname } } as never);
        }

        toast.error(message, {
          position: TABLE_CONFIG.TOAST_ERROR_POSITION,
          duration: TABLE_CONFIG.DEFAULT_TOAST_DURATION,
        });
      }
    },
    [silent, onError, navigate, location, toast, logger],
  );

  return { handleError };
}
